import React, { useState, useEffect, useCallback } from "react";
import {
  getData,
  getconferenceEvent,
  getwebinarEvent, 
  getRevenueData,
} from "../../../../services/event/dashboard/dashboardServices";
import Chart from "react-apexcharts";
import Sidebar from "../../../../components/Layout/Sidebar";
import Header from "../../../../components/Layout/Header";
import { Col, Container, Row } from "react-bootstrap";
import Card from "react-bootstrap/Card";
import BrochureCountriesChart from "./brochureCountriesChart";
import AbstractSubmittedChart from "./abstractSubmittedCountriesChart";
import RevenueCountryChart from "./revenueCountriesChart";
import AccommodationChart from "./accomodationChart";
import OccupancyChart from "./occupancyChart";
import EventTypeChart from "./eventTypeChart";
import Footer from "../../../../components/Layout/Footer";
import RevenueLeadByCurrency from "./revenueLeadByCurrency";
import RevenueLeadByPaymentType from "./revenueLeadByPaymentType";
import { formatDate } from "../../../../utils/dateUtils";
import Spinner from "../../../../components/Spinner";

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const subDomain = localStorage.getItem("sub_domain") || "";
  const [eventDetails, setEventDetails] = useState({
  conference: [], // Empty object initially
  webinar: [],
});

  const [revenueData, setRevenueData] = useState([]);
  const [startMonthInChart, setStartMonthInChart] = useState("");
  const [endMonthInChart] = useState(
    new Date().toISOString().split("T")[0]
  );
  const defaultOptions = {};

  const defaultSeries = [];

  const [chartData, setChartData] = useState({
    series: defaultSeries,
    options: defaultOptions,
  });

  //fetch event details
  const fetchEventDetails = useCallback(async () => {
    try {
      const [conferenceData, webinarData] = await Promise.all([
        getconferenceEvent({ sub_domain: subDomain }),
        getwebinarEvent({ sub_domain: subDomain }),
      ]);
      const conference = conferenceData[0] || {};
      const webinar = webinarData[0] || {};

      setEventDetails({ conference, webinar });


      // Set Start Month dynamically
      if (conference.conf_work_starts) {
        setStartMonthInChart(conference.conf_work_starts);
      }
      // ✅ dynamically set conf_work_starts as chart start date
      if (conference.conf_work_starts) {
        setStartMonthInChart(conference.conf_work_starts);
      }
    } catch (error) {
      console.error("Error fetching event details:", error);
    }
  }, [subDomain]);


  const fetchRevenueData = useCallback(async () => {
    try {
      const response = await getRevenueData({
        sub_domain: subDomain,
        start_month: startMonthInChart,
        end_month: endMonthInChart,
      });

      if (response && Array.isArray(response.data)) {
        setRevenueData(response.data);
      } else {
        setRevenueData([]);
      }
    } catch (error) {
      console.error("Error fetching revenue data:", error);
      setRevenueData([]);
    }
  },[subDomain,startMonthInChart,endMonthInChart]);

  //calculate days
  const calculateDaysLeft = (eventDate) => {
  const today = new Date();
  const endDate = new Date(eventDate);
  const diffTime = endDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays > 0 ? diffDays : 0; // Show 0 if past
  };

  //format date
  const formatDateRange = (fromDate, toDate) => {
    if (!fromDate || !toDate) return "";

    const from = new Date(fromDate);
    const to = new Date(toDate);

    const monthName = from.toLocaleString("default", { month: "long" });
    const fromDay = from.getDate();
    const toDay = to.getDate();
    const year = from.getFullYear();

    // If same date, show only fromDay
    if (fromDay === toDay) {
      return `${monthName} ${fromDay}, ${year}`;
    }

    return `${monthName} ${fromDay}-${toDay}, ${year}`;
  };

  const fetchData = useCallback(async () => {
    try {
      const response = await getData({
        sub_domain: subDomain,
        orderField: "month",
        orderDirection: "DESC",
        startMonth: startMonthInChart,
        endMonth: endMonthInChart,
      });

      if (response) {
        const { series, categories, startMonth } = response;

        setChartData((prev) => ({
          series,
          options: {
            ...prev.options,
            xaxis: { categories },
            stroke: {
              width: [2, 2, 2],
              curve: "smooth",
              dashArray: [0, 5, 0],
            },
            markers: { size: 5 },
            fill: {
              type: "gradient",
              gradient: {
                shade: "light",
                type: "vertical",
                shadeIntensity: 0.3,
                opacityFrom: 0.3,
                opacityTo: 0.05,
                stops: [0, 90, 100],
              },
            },
            tooltip: {
              shared: true,
              intersect: false,
              marker: {
                show: true,
                fillColors: ["#FF9800", "#008FFB", "#00E396"],
              },
            },
            legend: {
              position: "top",
              horizontalAlign: "left",
            },
            dataLabels: {
              enabled: true,
              style: {
                fontSize: "12px",
                colors: ["#FF9800", "#008FFB", "#00E396"],
              },
            },
            annotations: {
              xaxis: [
                {
                  x: startMonth,
                  borderColor: "#775DD0",
                  label: {
                    style: { color: "#fff", background: "#775DD0" },
                    text: "Work Starts",
                  },
                },
              ],
            },
          },
        }));
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  }, [subDomain,startMonthInChart,endMonthInChart]);

 useEffect(() => {
  const fetchAllData = async () => {
    setLoading(true); // Start loading state

    await Promise.all([fetchEventDetails(), fetchRevenueData(), fetchData()]);

    await new Promise((resolve) => setTimeout(resolve, 1000)); // Optional: delay to simulate API response time

    setLoading(false); // Stop loading after fetching all data
  };

  fetchAllData();
}, [fetchEventDetails, fetchRevenueData, fetchData, startMonthInChart, endMonthInChart]);

  return (
    <Container fluid className="p-0" style={{ maxWidth: "100%" }}>
      <Row>
        <Col>
          <div className="d-flex">
            <Sidebar />
            <div className="main-content w-100">
              <Header />
              <div className="container-fluid p-0" style={{ maxWidth: "100%" }}>
                {loading ? (
                  <div
                    className="d-flex justify-content-center align-items-center w-100"
                    style={{ minHeight: "300px" }}
                  >
                    <Spinner /> {/* Centered Spinner */}
                  </div>
                ) : (
                  <div>
                    <Container fluid>
                      <Row>
                        <Col md={6} lg={6}>
                          {/* <Breadcrumb /> */}
                        </Col>
                        <Col md={6}>
                          <div
                            className="text-end pt-2 pb-2 pr-0 pl-0"
                            style={{
                              color: "var(--blue_dark)",
                              fontSize: "var(--smfontSize)",
                            }}
                          >
                            <b>
                              Work starts from:{" "}
                              {startMonthInChart
                                ? formatDate(startMonthInChart)
                                : ""}{" "}
                              &nbsp;
                            </b>
                          </div>
                        </Col>
                      </Row>
                    </Container>
                    {/* event Details shown */}
                    <Container fluid className="p-2">
                    <Row className="g-0">
                      <Col md={12} lg={12} className="p-0">
                        <Card>
                          <Card.Header
                              className="bg-secondary pt-2 pb-1 text-white"
                              style={{ fontSize: "var(--smfontSize)",fontWeight:"var(--smfontWeight)" }}
                            >
                              Event Details
                            </Card.Header>
                          <Card.Body>
                            <Row>
                              {/* Left Column: Conference & Webinar */}
                              <Col md={6}>
                                {/* Conference Section */}
                                {eventDetails.conference && Object.keys(eventDetails.conference).length > 0 ? (
                                  <Card className="border mb-4">
                                    <Card.Body>
                                      <Row className="align-items-center">
                                        <Col md={3}>
                                          <Card className="text-center border shadow-sm">
                                            <Card.Header style={{ backgroundColor: "#FFC107", color: "#2c3e50", fontWeight: "bold", fontSize: "2rem" }}>
                                              {eventDetails.conference.from_date ? calculateDaysLeft(eventDetails.conference.from_date) : "-"}
                                            </Card.Header>
                                            <Card.Footer style={{ backgroundColor: "#fff", color: "#2c3e50", fontWeight: "600" }}>Days Left</Card.Footer>
                                          </Card>
                                        </Col>
                                        <Col md={9} style={{ lineHeight: "40px", position: "relative", bottom: "10px" }}>
                                          <div className="text-start">
                                            <h6 style={{ lineHeight:"32px",fontSize: "21px", fontWeight: "bold" }}>
                                              <i className="fa fa-globe"></i> {eventDetails.conference.event_title}
                                            </h6>
                                            <p className="mb-0" style={{ fontSize: "18px" }}>
                                              <i className="fa-solid fa-calendar"></i> &nbsp;  {formatDateRange(eventDetails.conference.from_date, eventDetails.conference.to_date)}


                                            </p>
                                            <p className="mb-0" style={{ fontSize: "18px" }}>
                                              <i className="fa-solid fa-location-dot"></i> &nbsp;{eventDetails.conference.venue}
                                            </p>
                                          </div>
                                        </Col>
                                      </Row>
                                    </Card.Body>
                                  </Card>
                                ) : (
                                  <p>No conference data available</p>
                                )}

                                {/* Webinar Section */}
                                {eventDetails.webinar && Object.keys(eventDetails.webinar).length > 0 ? (
                                  <Card className="border">
                                    <Card.Body>
                                      <Row className="align-items-center">
                                        <Col md={3}>
                                          <Card className="text-center border shadow-sm">
                                            <Card.Header style={{ backgroundColor: "#FFC107", color: "#2c3e50", fontWeight: "bold", fontSize: "2rem" }}>
                                              {eventDetails.webinar.from_date ? calculateDaysLeft(eventDetails.webinar.from_date) : "-"}
                                            </Card.Header>
                                            <Card.Footer style={{ backgroundColor: "#fff", color: "#2c3e50", fontWeight: "600" }}>Days Left</Card.Footer>
                                          </Card>
                                        </Col>
                                        <Col md={9} style={{ lineHeight: "40px", position: "relative", bottom: "10px" }}>
                                          <div className="text-start">
                                            <h6 style={{ lineHeight:"32px", fontSize: "21px", fontWeight: "bold" }}>
                                              <i className="fa fa-globe"></i> {eventDetails.webinar.event_title}
                                            </h6>
                                            <p className="mb-0" style={{ fontSize: "18px" }}>
                                              <i className="fa-solid fa-calendar"></i> &nbsp;  {formatDateRange(eventDetails.webinar.from_date, eventDetails.webinar.to_date)}


                                            </p>
                                          </div>
                                        </Col>
                                      </Row>
                                    </Card.Body>
                                  </Card>
                                ) : (
                                  <p>No webinar data available</p>
                                )}
                              </Col>

                              {/* Right Column: Revenue */}
                             <Col md={6}>
                              <h2 className="mt-0 text-grey" style={{fontWeight: "bold"}}>Revenue</h2>
                              <Row>
                                {revenueData.map((item, index) => (
                                  <Col key={index} md={6} className="mb-4">
                                    <Card className="mb-0">
                                      <Card.Body className="border rounded">
                                        <Row className="align-items-center">
                                          <Col className="text-center">
                                            <span
                                              style={{
                                                fontSize: "2rem",
                                                fontWeight: "bold",
                                                borderRadius: "0.25rem",
                                                backgroundImage: "linear-gradient(to top, #cfd9df 0%, #e2ebf0 100%)",
                                                display: "inline-block",
                                                padding: "10px 15px"
                                              }}
                                            >
                                              {item.symbol} <span style={{ fontWeight: "var(--mdfontWeight)", fontSize: "var(--mdfontSize)" }}>
                                              {item.amount}
                                            </span>
                                            </span>
                                          </Col>
                                          {/* <Col md={9}>
                                            
                                          </Col> */}
                                        </Row>
                                      </Card.Body>
                                    </Card>
                                  </Col>
                                ))}
                              </Row>
                            </Col>

                            </Row>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  </Container>
                  
                    {/* workflow analytics chart */}
                    <div className="container-fluid">
                      <div
                        className="row justify-content-center align-items-center"
                        style={{ minHeight: "60vh" }}
                      >
                        <div className="p-3">
                          <Card>
                            <Card.Header
                              className="bg-secondary pt-2 pb-1 text-white"
                              style={{ fontSize: "var(--smfontSize)",fontWeight:"var(--smfontWeight)" }}
                            >
                              Workflow Analytics
                            </Card.Header>
                            <Card.Body>
                              <Chart
                                options={chartData.options}
                                series={chartData.series}
                                type="line"
                                height={350}
                              />
                            </Card.Body>
                          </Card>

                          <Container fluid>
                            <Row className="g-3">
                              <Col md={4} style={{ padding: "0px" }}>
                                <Card style={{ overflow: "hidden" }}>
                                  <Card.Header className="bg-secondary pt-2 pb-1  text-white" style={{fontSize:"var(--smfontSize)",fontWeight:"var(--smfontWeight)"}}>
                                    Brochure Downloaded Countries
                                  </Card.Header>
                                  <Card.Body style={{ paddingLeft: "10px" }}>
                                    <BrochureCountriesChart
                                      height={350}
                                      subDomain={subDomain}
                                      filterField="createdAt"
                                      startMonth={startMonthInChart}
                                      endMonth={endMonthInChart}
                                    />
                                  </Card.Body>
                                </Card>
                              </Col>

                              <Col md={4} style={{ paddingRight: "0px" }}>
                                <Card>
                                  <Card.Header className="bg-secondary pt-2 pb-1  text-white" style={{ fontSize: "var(--smfontSize)",fontWeight:"var(--smfontWeight)" }}>
                                    Abstract Submitted Countries
                                  </Card.Header>
                                  <Card.Body style={{ paddingLeft: "10px" }}>
                                    <AbstractSubmittedChart
                                      height={350}
                                      subDomain={subDomain}
                                      filterField="abstract_date"
                                      startMonth={startMonthInChart}
                                      endMonth={endMonthInChart}
                                    />
                                  </Card.Body>
                                </Card>
                              </Col>

                              <Col md={4} style={{ paddingRight: "0px" }}>
                                <Card>
                                  <Card.Header className="bg-secondary pt-2 pb-1  text-white" style={{ fontSize: "var(--smfontSize)",fontWeight:"var(--smfontWeight)" }}>
                                    Revenue Country Count
                                  </Card.Header>
                                  <Card.Body style={{ paddingLeft: "10px" }}>
                                    <RevenueCountryChart
                                      height={350}
                                      subDomain={subDomain}
                                      filterField="date_of_payment"
                                      startMonth={startMonthInChart}
                                      endMonth={endMonthInChart}
                                    />
                                  </Card.Body>
                                </Card>
                              </Col>
                            </Row>
                          </Container>
                        </div>
                        {/* )} */}
                        <div className="p-3">
                          <Container fluid>
                            <Row className="g-3">
                              <Col md={4} style={{ paddingRight: "0px" }}>
                                <Card
                                  style={{ height: "450px", width: "100%" }}
                                >
                                  <Card.Header className="bg-secondary p-2  text-white" style={{ fontSize: "var(--smfontSize)",fontWeight:"var(--smfontWeight)" }}>
                                    Revenue Leads by Currency
                                  </Card.Header>
                                  <Card.Body>
                                    <div className="text-center">
                                      <RevenueLeadByCurrency
                                        subDomain={subDomain}
                                        startMonth={startMonthInChart}
                                        endMonth={endMonthInChart}
                                      />
                                    </div>
                                  </Card.Body>
                                </Card>
                              </Col>

                              <Col md={4} style={{ paddingRight: "0px" }}>
                                <Card
                                  style={{ height: "450px", width: "100%" }}
                                >
                                  <Card.Header className="bg-secondary p-2  text-white" style={{ fontSize: "var(--smfontSize)",fontWeight:"var(--smfontWeight)" }}>
                                    Revenue Leads by Payment Type
                                  </Card.Header>
                                  <Card.Body>
                                    <RevenueLeadByPaymentType
                                      subDomain={subDomain}
                                      startMonth={startMonthInChart}
                                      endMonth={endMonthInChart}
                                    />
                                  </Card.Body>
                                </Card>
                              </Col>
                              <Col md={4} style={{ paddingRight: "0px" }}>
                                <Card>
                                  <Card.Header className="bg-secondary p-2  text-white" style={{ fontSize: "var(--smfontSize)",fontWeight:"var(--smfontWeight)" }}>
                                    Accommodation Status
                                  </Card.Header>
                                  <Card.Body style={{ width: "100%" }}>
                                    <AccommodationChart
                                      subDomain={subDomain}
                                      filterField="date_of_payment"
                                      startMonth={startMonthInChart}
                                      endMonth={endMonthInChart}
                                    />
                                  </Card.Body>
                                </Card>
                              </Col>
                            </Row>
                          </Container>
                        </div>

                        <div className="p-3">
                          <Container fluid>
                            <Row className="g-3">
                              <Col md={4} style={{ paddingRight: "0px" }}>
                                <Card>
                                  <Card.Header className="bg-secondary p-2  text-white" style={{ fontSize: "var(--smfontSize)",fontWeight:"var(--smfontWeight)" }}>
                                    Total No of Nights by Occupancy Type
                                  </Card.Header>
                                  <Card.Body style={{ width: "100%" }}>
                                    <OccupancyChart
                                      subDomain={subDomain}
                                      startMonth={startMonthInChart}
                                      endMonth={endMonthInChart}
                                    />
                                  </Card.Body>
                                </Card>
                              </Col>
                              <Col md={4} style={{ paddingRight: "0px" }}>
                                <Card>
                                  <Card.Header className="bg-secondary p-2  text-white" style={{ fontSize: "var(--smfontSize)",fontWeight:"var(--smfontWeight)" }}>
                                    Event Type
                                  </Card.Header>
                                  <Card.Body
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      maxHeight: "400px",
                                    }}
                                  >
                                    <EventTypeChart
                                      height={350}
                                      subDomain={subDomain}
                                      startMonth={startMonthInChart}
                                      endMonth={endMonthInChart}
                                    />
                                  </Card.Body>
                                </Card>
                              </Col>
                            </Row>
                          </Container>
                        </div>
                        <Footer />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
